import React, { useEffect, useState } from "react";
import { X, DollarSign, Calendar, Truck, Tag, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";

import { equipmentApi, formatApiError } from "@/lib/api";
import CategorySelect from "./CategorySelect";

/**
 * One-stop equipment editor — opens when an inventory card is clicked.
 *
 * Edits every persistable field of one equipment item in a single dialog:
 *   label · category · pool · acquisition kind · cost · status · ETA ·
 *   vendor · notes.
 *
 * Live "available" + shortfall warning derived from props.
 * Has an explicit DELETE row at the bottom (guarded by a confirm dialog
 * upstream — the parent passes `onDelete`).
 *
 * Controlled save: writes go via `equipmentApi.update`; on success calls
 * `onMutated` and closes. Esc + click-outside cancel.
 */
export default function EquipmentEditDialog({
  open,
  equipment,
  used,
  categories,
  projectId,
  statuses = ["planned", "ordered", "shipped", "arrived"],
  onClose,
  onMutated,
  onDelete,
}) {
  // Local draft seeded from props on open. We don't auto-save on every edit
  // because the user might want to abandon — Save commits, Cancel discards.
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && equipment) {
      setDraft({
        label: equipment.label || equipment.type || "",
        quantity_total: String(equipment.quantity_total ?? 1),
        category: equipment.category || "",
        acquisition_kind: equipment.acquisition_kind || "purchased",
        cost_amount: equipment.cost_amount != null ? String(equipment.cost_amount) : "",
        cost_currency: equipment.cost_currency || "USD",
        status: equipment.status || statuses[0] || "planned",
        eta_date: equipment.eta_date || "",
        vendor: equipment.vendor || "",
        notes: equipment.notes || "",
      });
    }
  }, [open, equipment, statuses]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft]);

  if (!open || !equipment || !draft) return null;

  const pool = parseInt(draft.quantity_total, 10) || 0;
  const available = pool - used;
  const shortfall = available < 0;

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const costRaw = draft.cost_amount;
      const costParsed = costRaw === "" || costRaw == null ? null : parseFloat(costRaw);
      await equipmentApi.update(equipment.id, {
        label: draft.label.trim() || equipment.type,
        quantity_total: Math.max(0, pool),
        category: draft.category, // "" clears
        acquisition_kind: draft.acquisition_kind,
        // null/NaN -> omit (backend treats unset as no-change). Send valid number otherwise.
        ...(costParsed != null && !Number.isNaN(costParsed) ? { cost_amount: costParsed } : {}),
        cost_currency: draft.cost_currency,
        status: draft.status,
        eta_date: draft.eta_date,
        vendor: draft.vendor,
        notes: draft.notes,
      });
      onMutated && (await onMutated());
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="equipment-edit-overlay"
    >
      <div
        className="bg-bg-elevated border border-amber min-w-[480px] max-w-[520px] w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="equipment-edit-dialog"
      >
        <header className="px-3 py-2 border-b border-line-strong sticky top-0 bg-bg-elevated flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">EQUIPMENT</span>
            <span className="text-ink-mute mono text-2xs">·</span>
            <span className="mono text-2xs text-ink-mute truncate">{equipment.type}</span>
          </div>
          <button onClick={onClose} className="text-ink-mute hover:text-ink" data-testid="equipment-edit-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <Field label="LABEL" testid="ed-label">
            <input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              className="w-full bg-bg-base border border-line-strong focus:border-amber outline-none text-ink text-sm px-2 py-1.5"
              data-testid="ed-label-input"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="POOL QTY" testid="ed-pool">
              <input
                type="number"
                value={draft.quantity_total}
                onChange={(e) => setDraft({ ...draft, quantity_total: e.target.value })}
                className="w-full bg-bg-base border border-line-strong focus:border-amber outline-none text-ink mono text-sm px-2 py-1.5 tabular-nums"
                data-testid="ed-pool-input"
                min="0"
              />
              <div className="mono text-2xs uppercase tracking-widest mt-1">
                <span className="text-ink-mute">{used} USED · </span>
                <span className={shortfall ? "text-red font-semibold" : "text-cyan"}>
                  {shortfall ? `${available} SHORT` : `${available} AVAIL`}
                </span>
              </div>
            </Field>
            <Field label="CATEGORY" testid="ed-category">
              <CategorySelect
                value={draft.category}
                categories={categories.map((c) => (typeof c === "string" ? c : c.name))}
                onChange={(v) => setDraft({ ...draft, category: v })}
                onCreate={async (name) => {
                  try {
                    await equipmentApi.createCategory(projectId, name);
                    // Categories are surfaced via parent /full refresh; the
                    // value gets set straight away regardless.
                    onMutated && onMutated();
                  } catch (e) {
                    toast.error(formatApiError(e) || "Create category failed");
                    throw e;
                  }
                }}
                buttonClassName="w-full"
                menuWidth={240}
                testid="ed-category-select"
              />
            </Field>
          </div>

          {/* Acquisition / cost block */}
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Field label="ACQUISITION" testid="ed-kind">
              <div className="flex border border-line-strong">
                {["hired", "purchased"].map((k) => (
                  <button
                    key={k}
                    onClick={() => setDraft({ ...draft, acquisition_kind: k })}
                    data-testid={`ed-kind-${k}`}
                    className={clsx(
                      "mono text-2xs uppercase tracking-widest px-3 py-1.5 flex-1 transition-colors",
                      draft.acquisition_kind === k
                        ? (k === "hired" ? "bg-amber text-bg-base" : "bg-emerald-400 text-bg-base")
                        : "text-ink-mute hover:text-ink"
                    )}
                  >
                    {k === "hired" ? "HIRED" : "PURCHASED"}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={draft.acquisition_kind === "hired" ? "HIRE COST" : "PURCHASE PRICE"} testid="ed-cost">
              <div className="flex items-center bg-bg-base border border-line-strong focus-within:border-amber w-32">
                <DollarSign className="w-3 h-3 text-ink-mute ml-1.5 shrink-0" />
                <input
                  value={draft.cost_amount}
                  onChange={(e) => setDraft({ ...draft, cost_amount: e.target.value })}
                  placeholder="—"
                  className="bg-transparent text-ink mono text-sm px-1.5 py-1.5 w-full tabular-nums outline-none"
                  data-testid="ed-cost-input"
                />
              </div>
            </Field>
          </div>

          {/* Procurement state block */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="STATUS" testid="ed-status">
              <select
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="w-full bg-bg-base border border-line-strong focus:border-amber outline-none mono text-xs uppercase tracking-widest px-2 py-1.5"
                data-testid="ed-status-select"
              >
                {statuses.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </Field>
            <Field label="ETA" testid="ed-eta">
              <div className="flex items-center bg-bg-base border border-line-strong focus-within:border-amber">
                <Calendar className="w-3 h-3 text-ink-mute ml-1.5 shrink-0" />
                <input
                  type="date"
                  value={draft.eta_date}
                  onChange={(e) => setDraft({ ...draft, eta_date: e.target.value })}
                  className="bg-transparent text-ink mono text-xs px-1.5 py-1.5 w-full outline-none"
                  data-testid="ed-eta-input"
                />
              </div>
            </Field>
          </div>

          <Field label="VENDOR" testid="ed-vendor">
            <div className="flex items-center bg-bg-base border border-line-strong focus-within:border-amber">
              <Truck className="w-3 h-3 text-ink-mute ml-1.5 shrink-0" />
              <input
                value={draft.vendor}
                onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
                placeholder="Hire company or supplier"
                className="bg-transparent text-ink text-sm px-1.5 py-1.5 w-full outline-none"
                data-testid="ed-vendor-input"
              />
            </div>
          </Field>

          <Field label="NOTES" testid="ed-notes">
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={3}
              placeholder="Quirks, riders, serial numbers…"
              className="w-full bg-bg-base border border-line-strong focus:border-amber outline-none text-ink text-sm px-2 py-1.5 resize-none"
              data-testid="ed-notes-input"
            />
          </Field>

          {/* Shortfall banner — only when over-allocated */}
          {shortfall && (
            <div className="flex items-center gap-2 px-3 py-2 border border-red bg-red-glow/10 text-red mono text-2xs uppercase tracking-widest">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              {Math.abs(available)} UNITS SHORT — INCREASE POOL OR REDUCE ALLOCATIONS
            </div>
          )}
        </div>

        <footer className="px-3 py-2 border-t border-line-strong sticky bottom-0 bg-bg-elevated flex items-center gap-2">
          <button
            onClick={() => onDelete && onDelete()}
            className="mono text-2xs uppercase tracking-widest px-2 py-1.5 text-red border border-red/40 hover:bg-red-glow/15 flex items-center gap-1"
            data-testid="ed-delete"
            title="Delete equipment + cascade allocations"
          >
            <Trash2 className="w-3 h-3" /> DELETE
          </button>
          <div className="flex-1" />
          <span className="mono text-2xs uppercase tracking-widest text-ink-mute">⌘↩ save · esc cancel</span>
          <button
            onClick={onClose}
            className="mono text-2xs uppercase tracking-widest px-3 py-1.5 border border-line text-ink-mute hover:text-ink hover:border-ink-mute"
            data-testid="ed-cancel"
          >
            CANCEL
          </button>
          <button
            onClick={submit}
            disabled={busy || !draft.label.trim()}
            className="mono text-2xs uppercase tracking-widest px-4 py-1.5 bg-amber text-bg-base hover:bg-amber-light disabled:opacity-40"
            data-testid="ed-save"
          >
            SAVE
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">{label}</div>
      {children}
    </div>
  );
}
